<template>
    <ClientOnly v-if="isEditionEnabled">
        <VJoyButton
            :aria-label="t('profile.section.header.recommendations.ask.label')"
            icon="plus-circle"
            size="small"
            variant="ghost"
            data-testid="open-recommendation-request"
            class="recommendation-button"
            @click="openEditRecommendation"
        >
            {{ t('profile.section.header.recommendations.ask.label') }}
        </VJoyButton>
        <template #fallback>
            <SectionActionSkeleton />
        </template>
    </ClientOnly>
    <template v-else-if="isRecommendationEnabled">
        <ClientOnly>
            <VJoyDialogTrigger v-if="hasAlreadyRecommendedThisProfile" :dialog="removeOwnDialog">
                <VJoyButton
                    :icon="deletionIcon"
                    data-testid="recommendation-own-delete"
                    size="small"
                    variant="ghost"
                    class="recommendation-button"
                    @mouseout="hideDeleteRecommendationOption"
                    @mouseover="showDeleteRecommendationOption"
                >
                    {{ t('profile.section.recommendation.cta.done.label', {0: profile?.firstname}) }}
                </VJoyButton>
            </VJoyDialogTrigger>
            <VJoyButton
                v-else
                :aria-label="t('profile.section.recommendation.cta.label', {0: profile?.firstname})"
                icon="add"
                size="small"
                variant="ghost"
                class="recommendation-button"
                data-testid="add-recommendation"
                @click="emit('open-add-recommendation')"
            >
                {{ t('profile.section.recommendation.cta.label', {0: profile?.firstname}) }}
            </VJoyButton>
            <template #fallback>
                <SectionActionSkeleton />
            </template>
        </ClientOnly>
        <VJoyDialog
            id="removalOwnRecommendationConfirmDialog"
            ref="removeOwnDialog"
            :cancel-text="t('general.cancel')"
            :confirm-text="t('general.continue')"
            pattern-confirm
        >
            <template #dialog-header>
                <p>{{ t('generic.form.confirm.text') }}</p>
            </template>

            <template #dialog-confirm>
                <VJoyButton data-testid="remove-own-recommendation-confirm" size="small" variant="main" @click="deleteOwnRecommendationAction">
                    {{ t('general.continue') }}
                </VJoyButton>
            </template>
        </VJoyDialog>
    </template>
    <ClientOnly v-if="isTranslationEnabled">
        <TranslateButton section="recommendation" />
        <template #fallback>
            <SectionActionSkeleton />
        </template>
    </ClientOnly>
</template>

<script lang="ts" setup>
    import {computed, onMounted, onUnmounted, ref} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useProfilePageViewMode, useTranslation} from '#imports';
    import {VJoyButton, VJoyDialog, VJoyDialogTrigger} from '@maltjoy/core-vue';
    import TranslateButton from '~/components/main/TranslateButton.vue';
    import SectionActionSkeleton from '~/components/section/SectionActionSkeleton.vue';
    import {useProfileTracking} from '~/composables/useProfileTracking';
    import {eventBus} from '~/config/events';
    import {RecommendationDeletedBy, RecommendationRequestStartedEntryPoint} from '~/config/TrackingEvents';
    import {useRecommendationsStore} from '~/features/recommendations/store/recommendations.store';
    import {useProfileStore} from '~/store/profileStore';
    import {TranslationDirection, useTranslationStore} from '~/store/translation/translationStore';

    const emit = defineEmits(['open-add-recommendation']);

    // Stores
    const profileStore = useProfileStore();
    const {profile} = storeToRefs(profileStore);
    const recommendationsStore = useRecommendationsStore();
    const {recommendations} = storeToRefs(recommendationsStore);

    // Composables
    const {t} = useTranslation();
    const {isEditionEnabled, isRecommendationEnabled, isTranslationEnabled} = useProfilePageViewMode();
    const {translateAll, replaceTranslationContent} = useTranslationStore();
    const {trackRecommendationDeleted} = useProfileTracking();

    // Refs
    const deletionOption = ref(false);
    const removeOwnDialog = ref<InstanceType<typeof VJoyDialog> | null>(null);

    // Computed values
    const deletionIcon = computed(() => (deletionOption.value ? 'trash' : 'check'));
    const hasAlreadyRecommendedThisProfile = computed(() =>
        recommendations.value?.recommendations.some((reco) => reco.links.some((hateoasLink: any) => hateoasLink.rel === 'deleteRecommendation')),
    );

    function openEditRecommendation() {
        if (profile.value?.editMode === true) {
            eventBus.emit('recommendations:edit-mode', {entryPoint: RecommendationRequestStartedEntryPoint.SECTION_EDIT});
        }
    }

    /**
     * Delete own recommendation
     */
    function deleteOwnRecommendationAction() {
        const recommendationToDelete = recommendations.value?.recommendations.find((reco) =>
            reco.links.some((hateoasLink: any) => hateoasLink.rel === 'deleteRecommendation'),
        );
        if (!recommendationToDelete) {
            return;
        }
        recommendationsStore.deleteRecommendation(recommendationToDelete);
        trackRecommendationDeleted(profile.value!.profileId, RecommendationDeletedBy.AUTHOR, recommendationToDelete.experienceId);
        removeOwnDialog.value?.hide();
    }

    function showDeleteRecommendationOption() {
        deletionOption.value = true;
    }

    function hideDeleteRecommendationOption() {
        deletionOption.value = false;
    }

    /**
     * Translate recommendations
     */
    const translationSection = 'recommendations';
    const translationField = 'textContent';

    async function translateRecommendations() {
        const recommendationTextContentRequests =
            recommendations.value?.recommendations.map((recommendation, index) => {
                return {
                    section: translationSection,
                    field: translationField,
                    index,
                    originalContent: recommendation.textContent ?? ' ',
                };
            }) || [];

        await translateAll(recommendationTextContentRequests);
        replaceTranslationContent(translationSection, translationField, TranslationDirection.TRANSLATE, switchTranslation);
    }

    function restoreRecommendations() {
        replaceTranslationContent(translationSection, translationField, TranslationDirection.RESTORE, switchTranslation);
    }

    function switchTranslation(content: string, index: number) {
        recommendations.value!.recommendations[index].textContent = content;
    }

    onMounted(() => {
        eventBus.on('translation:request', translateRecommendations);
        eventBus.on('translation:restore', restoreRecommendations);
    });

    onUnmounted(() => {
        eventBus.off('translation:request', translateRecommendations);
        eventBus.off('translation:restore', restoreRecommendations);
    });
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint' as joy;

    .recommendation-button {
        :deep(.joy-button--slot) {
            display: none;

            @include joy.screen_sm(min) {
                display: inline;
            }
        }
    }
</style>
